import React from 'react';
import { take, put, call, select, takeLatest, fork } from 'redux-saga/effects';
import { push } from 'connected-react-router';

import { sendErrorToSentry } from 'js/logging';
import { ROUTE_CODES } from 'js/config/routes';
import ScribeModel from 'js/models/ScribeModel';
import { updateProject } from 'js/shared/helpers/app-services/AppServices/projects';

import {
  CREATE_SCRIBE_FROM_TEMPLATE,
  createScribeFromTemplateSuccess,
  GET_SCRIBE_TEMPLATES,
  SAVE_SCRIBE_AS_TEMPLATE,
  saveScribeAsTemplateSuccess,
  saveScribeAsTemplateFailed,
  createScribeFromTemplateFailed
} from '../actionCreators/templatesActions';
import { showCallout, showError } from '../actionCreators/uiActions';
import { saveTemplate } from '../shared/providers/TemplatesManagementProvider';
import { appServices } from '../shared/helpers/app-services/AppServices';
import { PIN_ALL_CAMERA_BY_DEFAULT } from '../config/config';

import sanitizeScribeForTransport from './sagaHelpers/sanitizeScribeForTransport';
import { getTemplatesSaga } from './templatesSagas/getTemplatesSaga';

function* saveScribeAsTemplate(action) {
  try {
    const entry = yield call(saveTemplate, action.scribeId);

    yield put(saveScribeAsTemplateSuccess());
    yield put(
      showCallout({
        title: 'Project saved as a template',
        details: (
          <a href={entry.cmsURL} target="_blank" rel="noopenner noreferrer">
            <span>Project Template in CMS</span>
          </a>
        )
      })
    );
  } catch (error) {
    console.error(error);
    let description = error.message;
    yield put(saveScribeAsTemplateFailed(error));
    yield put(
      showError({
        message: 'Could not save project as a template',
        description
      })
    );
  }
}

function* createProjectFromServicesTemplate(servicesTemplateId, templateData) {
  const project = yield call(appServices.createProjectFromTemplate, servicesTemplateId);

  const newScribe = yield call(ScribeModel.fromObject, project, { pinAllCameras: PIN_ALL_CAMERA_BY_DEFAULT });

  if (!newScribe.source || newScribe.source.type !== 'template') {
    newScribe.source = {
      type: 'template',
      meta: {
        templateId: templateData.id,
        title: templateData.title
      }
    };
  }

  const sanitizedScribe = sanitizeScribeForTransport(newScribe);
  yield call(updateProject, sanitizedScribe, appServices.exchangeCognitoTokenForSparkolToken);

  yield put(createScribeFromTemplateSuccess(newScribe));

  yield put(push(ROUTE_CODES.EDITOR.replace(':scribeId', project.projectDataId)));
}

/**
 * This creates a blank Scribe without any element in it.
 * Needed as we have to create the Scribe first in the DB
 * before cloning the `template` elements.
 */
function* createScribeFromTemplate({ templateId }) {
  const stateTemplateData = yield select(state => {
    return (
      state.templates.templatesByCategory
        .flatMap(category => category.templates)
        .find(template => template.id === templateId)
    );
  });

  try {
    yield call(createProjectFromServicesTemplate, stateTemplateData.sparkolTemplateId, stateTemplateData);
  } catch (error) {
    yield put(createScribeFromTemplateFailed());
    sendErrorToSentry(error);
  }
}

// `takeLeading` is available in newer version of redux-saga but this is the low-level effects
// which are the same - as taken from https://redux-saga.js.org/docs/api/#:~:text=fetchUser)%0A%7D-,Notes,-takeLeading
const takeLeading = (patternOrChannel, saga, ...args) =>
  fork(function* () {
    while (true) {
      const action = yield take(patternOrChannel);
      yield call(saga, ...args.concat(action));
    }
  });

export default function* templatesSaga() {
  yield takeLeading(CREATE_SCRIBE_FROM_TEMPLATE, createScribeFromTemplate);
  yield takeLatest(GET_SCRIBE_TEMPLATES, getTemplatesSaga);
  yield takeLatest(SAVE_SCRIBE_AS_TEMPLATE, saveScribeAsTemplate);
}
